import { useEffect, useState } from "react";
import { Bot, Loader2, Save, Trash2, RefreshCw, Link2, Unlink, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const Card = ({ title, children, testId }) => (
  <section className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">{title}</h2>
    {children}
  </section>
);

const Hint = () => (
  <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="agent-uptime-hint">
    <Info className="mt-0.5 h-4 w-4 shrink-0" />
    <span>Bot und Postfach-Überwachung laufen, solange das Backend läuft. <b>Für den Dauerbetrieb muss die App veröffentlicht sein.</b> Danach hier die veröffentlichte Adresse eintragen und „Webhook neu registrieren“ drücken.</span>
  </div>
);

const Steps = () => (
  <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-slate-600" data-testid="agent-botfather-steps">
    <li>In Telegram <b>@BotFather</b> öffnen und <code>/newbot</code> senden.</li>
    <li>Namen (z. B. „Mein Beleg-Agent“) und einen Benutzernamen, der auf <code>bot</code> endet, wählen.</li>
    <li>Den angezeigten <b>Token</b> (z. B. <code>123456789:AA…</code>) hier einfügen und speichern. Er wird verschlüsselt gespeichert und nie wieder angezeigt.</li>
    <li>Danach den Kopplungs-Link öffnen oder den QR-Code scannen und in Telegram auf <b>Starten</b> tippen.</li>
  </ol>
);

const BotCard = ({ s, setS }) => {
  const [token, setToken] = useState("");
  const [base, setBase] = useState(s.public_base_url || window.location.origin);
  const [busy, setBusy] = useState("");
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { setS(await fn()); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <Card title="Telegram-Bot" testId="agent-bot-card">
      {!s.bot_configured && <Steps />}
      {s.bot_configured && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="agent-bot-status">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Verbunden mit <b>@{s.bot_username}</b>{s.mock && <span className="rounded bg-slate-100 px-1.5 text-xs">Simulation</span>}
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs" data-testid="agent-mode">{s.mode === "webhook" ? "Webhook aktiv" : "Long Polling"}</span>
        </div>
      )}
      {s.webhook_error && <p className="flex items-start gap-1.5 text-sm text-amber-800" data-testid="agent-webhook-error"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Webhook nicht gesetzt: {s.webhook_error} – Nachrichten werden per Long Polling abgeholt.</p>}
      <div className="grid gap-1.5">
        <Label className="text-sm font-semibold text-slate-700">{s.bot_configured ? "Neuen Bot-Token (optional)" : "Bot-Token"}</Label>
        <Input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder={s.bot_configured ? "••••••••" : "123456789:AA…"} className="h-11" data-testid="agent-token-input" />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-sm font-semibold text-slate-700">Öffentliche Adresse der App (für den Webhook)</Label>
        <Input value={base} onChange={(e) => setBase(e.target.value)} className="h-11" data-testid="agent-base-url-input" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button disabled={!token || !!busy} onClick={() => run("token", () => api.agentSetToken(token.trim(), base), "Bot verbunden").then(() => setToken(""))} className="h-11 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="agent-token-save-btn">
          {busy === "token" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Token speichern
        </Button>
        {s.bot_configured && <>
          <Button variant="outline" disabled={!!busy} onClick={() => run("wh", async () => { await api.agentSaveSettings({ public_base_url: base }); return api.agentWebhook(); }, "Webhook neu registriert")} className="h-11 gap-1.5" data-testid="agent-webhook-btn">
            {busy === "wh" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Webhook neu registrieren
          </Button>
          <Button variant="ghost" disabled={!!busy} onClick={() => run("del", api.agentDisconnect, "Bot getrennt")} className="h-11 gap-1.5 text-red-700 hover:bg-red-50" data-testid="agent-disconnect-btn"><Trash2 className="h-4 w-4" />Bot trennen</Button>
        </>}
      </div>
    </Card>
  );
};

const PairCard = ({ s, setS }) => (
  <Card title="Kopplung mit deinem Telegram-Chat" testId="agent-pair-card">
    {s.paired ? (
      <p className="flex items-center gap-2 text-sm" data-testid="agent-paired"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Gekoppelt mit <b>{s.chat_name}</b> seit {fmtDateTime(s.paired_at)}. Nur dieser Chat darf mit dem Bot sprechen.</p>
    ) : (
      <p className="text-sm text-slate-600">Noch kein Chat gekoppelt. Der erste Chat, der den Link öffnet und auf „Starten“ tippt, wird als einziger berechtigter Chat gespeichert.</p>
    )}
    {s.pair_link && (
      <div className="flex flex-wrap items-center gap-5" data-testid="agent-pair-link-box">
        <div className="rounded-lg border border-slate-200 bg-white p-2" data-testid="agent-pair-qr"><QRCodeSVG value={s.pair_link} size={132} /></div>
        <div className="grid gap-2 text-sm">
          <a href={s.pair_link} target="_blank" rel="noreferrer" className="break-all font-medium text-blue-700 underline" data-testid="agent-pair-link">{s.pair_link}</a>
          <span className="text-slate-500">{s.paired ? "Mit diesem Link einen anderen Chat koppeln (ersetzt den aktuellen)." : "Einmal-Code – nach der Kopplung ungültig."}</span>
        </div>
      </div>
    )}
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" onClick={async () => setS(await api.agentPair())} className="h-10 gap-1.5" data-testid="agent-repair-btn"><Link2 className="h-4 w-4" />{s.paired ? "Neu koppeln" : "Neuen Code erzeugen"}</Button>
      {s.paired && <Button variant="ghost" onClick={async () => { setS(await api.agentUnpair()); toast.success("Chat getrennt"); }} className="h-10 gap-1.5 text-red-700 hover:bg-red-50" data-testid="agent-unpair-btn"><Unlink className="h-4 w-4" />Chat trennen</Button>}
    </div>
  </Card>
);

const Num = ({ label, k, f, set, testId, type = "number", w = "w-24" }) => (
  <label className="grid gap-1 text-sm font-medium text-slate-700">{label}
    <Input type={type} value={f[k]} onChange={(e) => set({ ...f, [k]: type === "number" ? Number(e.target.value) : e.target.value })} className={`font-num h-10 ${w}`} data-testid={testId} />
  </label>
);

const Toggle = ({ label, k, f, set, testId }) => (
  <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><Switch checked={!!f[k]} onCheckedChange={(v) => set({ ...f, [k]: v })} data-testid={testId} />{label}</label>
);

const RulesCard = ({ s, setS }) => {
  const keys = ["max_per_day", "quiet_from", "quiet_to", "digest_enabled", "digest_time", "backlog_enabled", "backlog_per_day", "backlog_order", "active_from", "ai_daily_cap"];
  const [f, set] = useState(Object.fromEntries(keys.map((k) => [k, s[k]])));
  const [advanced, setAdvanced] = useState(!!s.backlog_enabled);
  const save = async () => { try { setS(await api.agentSaveSettings(f)); toast.success("Einstellungen gespeichert"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Nachfragen & Postfach-Überwachung" testId="agent-rules-card">
      <div className="flex flex-wrap gap-4">
        <Num label="Max. Nachrichten pro Tag" k="max_per_day" f={f} set={set} testId="agent-max-per-day" />
        <Num label="Ruhezeit von" k="quiet_from" f={f} set={set} type="time" w="w-32" testId="agent-quiet-from" />
        <Num label="bis" k="quiet_to" f={f} set={set} type="time" w="w-32" testId="agent-quiet-to" />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <Toggle label="Tagesübersicht senden" k="digest_enabled" f={f} set={set} testId="agent-digest-switch" />
        <Num label="um" k="digest_time" f={f} set={set} type="time" w="w-32" testId="agent-digest-time" />
      </div>
      <div className="grid gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="agent-cutoff-box">
        <div className="flex flex-wrap items-end gap-4">
          <Num label="Agent aktiv ab (Buchungsdatum)" k="active_from" f={f} set={set} type="date" w="w-44" testId="agent-active-from" />
          <Num label="KI-Aufrufe pro Tag (Agent)" k="ai_daily_cap" f={f} set={set} testId="agent-ai-cap" />
          <div className="pb-2 text-sm text-slate-700" data-testid="agent-ai-used">KI-Aufrufe heute: <b className="font-num">{s.ai_used_today ?? 0}/{s.ai_daily_cap}</b></div>
        </div>
        <p className="text-xs text-slate-600">Der Agent fragt nur bei Buchungen ab diesem Datum nach und sucht nur für diese automatisch im Postfach. Ältere Auszüge werden normal erklärt; Postfach-Suche und Übernahme bleiben manuell möglich.</p>
      </div>
      <Toggle label="Erweitert anzeigen" k="_adv" f={{ _adv: advanced }} set={(v) => setAdvanced(v._adv)} testId="agent-advanced-switch" />
      {advanced && <div className="grid gap-2 rounded-lg bg-slate-50 p-3" data-testid="agent-backlog-box">
        <p className="text-xs font-medium text-amber-800">Achtung: Der Rückstand fragt auch alte Buchungen vor dem Stichtag nach und verbraucht dafür KI-Guthaben.</p>
        <Toggle label="Rückstand abarbeiten (alte offene Buchungen nachfragen)" k="backlog_enabled" f={f} set={set} testId="agent-backlog-switch" />
        <div className="flex flex-wrap items-end gap-4">
          <Num label="pro Tag" k="backlog_per_day" f={f} set={set} testId="agent-backlog-per-day" />
          <label className="grid gap-1 text-sm font-medium text-slate-700">Reihenfolge
            <select value={f.backlog_order} onChange={(e) => set({ ...f, backlog_order: e.target.value })} className="h-10 rounded-md border border-input bg-white px-2 text-sm" data-testid="agent-backlog-order">
              <option value="oldest">Älteste zuerst</option><option value="amount">Größter Betrag zuerst</option>
            </select>
          </label>
        </div>
      </div>}
      <div className="grid gap-2 rounded-lg bg-slate-50 p-3">
      </div>
      <p className="text-xs text-slate-500">Zeiten in österreichischer Zeit (Europe/Vienna). In der Ruhezeit werden keine Nachrichten gesendet – sie kommen danach.</p>
      <div><Button onClick={save} className="h-10 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="agent-rules-save-btn"><Save className="h-4 w-4" />Speichern</Button></div>
    </Card>
  );
};

export default function AgentSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.agentSettings().then(setS); }, []);
  if (!s) return null;
  return (
    <div className="grid max-w-3xl gap-5 p-4 sm:p-6 lg:p-8 fade-up" data-testid="agent-settings-view">
      <div>
        <div className="text-sm text-slate-500">Einstellungen</div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"><Bot className="h-6 w-6" />Beleg-Agent</h1>
      </div>
      <Hint />
      <BotCard key={`${s.bot_username}-${s.public_base_url}`} s={s} setS={setS} />
      {s.bot_configured && <PairCard s={s} setS={setS} />}
      <RulesCard s={s} setS={setS} />
    </div>
  );
}
